import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  IconButton,
  Slider,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import {
  PlayArrow,
  Stop,
  Refresh,
  VolumeUp,
  Speed,
  RecordVoiceOver,
  ContentCopy
} from '@mui/icons-material';
import api from '../../../services/api';

const sampleTexts = [
  "I understand how you're feeling, and it's completely valid to feel this way.",
  "That sounds really challenging. Can you tell me more about what happened?",
  "Let's explore that together. What comes to mind when you think about it?",
  "It takes real courage to share something so personal. Thank you for trusting me.",
  "How did that make you feel in the moment?",
  "I hear what you're saying, and I want to make sure I understand correctly."
];

function VoiceTestingInterface({ token, configuration, providers }) {
  const [selectedProvider, setSelectedProvider] = useState('elevenlabs');
  const [selectedVoice, setSelectedVoice] = useState('');
  const [testText, setTestText] = useState(sampleTexts[0]);
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState(null);
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const [volume, setVolume] = useState(1.0);
  const audioRef = useRef(null);

  useEffect(() => {
    loadVoices();
  }, [selectedProvider]);

  const loadVoices = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/tts/voices?provider=${selectedProvider}`, token);
      setVoices(response.data.voices || []);
      if (response.data.voices?.length > 0) {
        setSelectedVoice(response.data.voices[0].id);
      }
      setError(null);
    } catch (err) {
      console.error('Failed to load voices:', err);
      setError('Failed to load voices for this provider');
      setVoices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTestVoice = async () => {
    if (!selectedVoice || !testText) {
      setError('Please select a voice and enter test text');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await api.post('/tts/test', {
        text: testText,
        voiceId: selectedVoice,
        provider: selectedProvider,
        role: 'ai_therapist'
      }, token);

      if (response.data.success) {
        // Convert base64 to audio and play
        const audioData = `data:audio/mp3;base64,${response.data.audio}`;
        
        if (audioRef.current) {
          audioRef.current.src = audioData;
          audioRef.current.playbackRate = playbackRate;
          audioRef.current.volume = volume;
          audioRef.current.play();
          setPlaying(true);
        }
      }
    } catch (err) {
      console.error('Failed to test voice:', err);
      setError('Failed to generate test audio');
    } finally {
      setLoading(false);
    }
  };

  const handleStopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setPlaying(false);
    }
  };

  const handleSampleTextSelect = (text) => {
    setTestText(text);
  };

  const getVoiceDetails = (voice) => {
    const details = [];
    if (voice.gender) details.push(voice.gender);
    if (voice.accent) details.push(voice.accent);
    if (voice.language) details.push(voice.language);
    return details.join(' • ');
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Voice Testing Interface
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Test different voices and providers with sample therapy text.
      </Typography>

      <Grid container spacing={3}>
        {/* Voice Selection */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Voice Selection
              </Typography>
              
              <FormControl fullWidth margin="normal">
                <InputLabel>Provider</InputLabel>
                <Select
                  value={selectedProvider}
                  onChange={(e) => setSelectedProvider(e.target.value)}
                  label="Provider"
                >
                  {providers.map(provider => (
                    <MenuItem key={provider.name} value={provider.name}>
                      {provider.displayName}
                      {provider.available && (
                        <Chip 
                          label="Available" 
                          size="small" 
                          color="success" 
                          sx={{ ml: 1 }}
                        />
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth margin="normal">
                <InputLabel>Voice</InputLabel>
                <Select
                  value={selectedVoice}
                  onChange={(e) => setSelectedVoice(e.target.value)}
                  label="Voice"
                  disabled={loading || voices.length === 0}
                >
                  {voices.map(voice => (
                    <MenuItem key={voice.id} value={voice.id}>
                      <Box>
                        <Typography>{voice.name}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {getVoiceDetails(voice)}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box mt={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={loadVoices}
                  disabled={loading}
                >
                  Refresh Voices
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Test Controls */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Playback Controls
              </Typography>

              <Box mt={2}>
                <Typography gutterBottom>
                  Speed: {playbackRate}x
                </Typography>
                <Slider
                  value={playbackRate}
                  onChange={(e, value) => setPlaybackRate(value)}
                  min={0.5}
                  max={2.0}
                  step={0.1}
                  marks
                  valueLabelDisplay="auto"
                />
              </Box>

              <Box mt={2}>
                <Typography gutterBottom>
                  Volume: {Math.round(volume * 100)}%
                </Typography>
                <Slider
                  value={volume}
                  onChange={(e, value) => setVolume(value)}
                  min={0}
                  max={1}
                  step={0.1}
                  marks
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
                />
              </Box>

              <Box mt={3} display="flex" gap={2}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={playing ? <Stop /> : <PlayArrow />}
                  onClick={playing ? handleStopAudio : handleTestVoice}
                  disabled={loading || !selectedVoice}
                  fullWidth
                >
                  {playing ? 'Stop' : 'Test Voice'}
                </Button>
              </Box>

              {loading && (
                <Box display="flex" justifyContent="center" mt={2}>
                  <CircularProgress size={24} />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Test Text */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Test Text
              </Typography>
              
              <TextField
                fullWidth
                multiline
                rows={3}
                value={testText}
                onChange={(e) => setTestText(e.target.value)}
                placeholder="Enter text to test..."
                variant="outlined"
                margin="normal"
                inputProps={{ maxLength: 500 }}
                helperText={`${testText.length}/500 characters`}
              />

              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Sample Texts
              </Typography>
              <List dense>
                {sampleTexts.map((text, index) => (
                  <React.Fragment key={index}>
                    <ListItem 
                      button 
                      onClick={() => handleSampleTextSelect(text)}
                      selected={testText === text}
                    >
                      <ListItemText 
                        primary={text}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                      <IconButton 
                        edge="end" 
                        size="small"
                        onClick={() => handleSampleTextSelect(text)}
                        sx={{ ml: 1 }}
                      >
                        <ContentCopy />
                      </IconButton>
                    </ListItem>
                    {index < sampleTexts.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        onEnded={() => setPlaying(false)}
        onError={(e) => {
          console.error('Audio playback error:', e);
          setError('Failed to play audio');
          setPlaying(false);
        }}
      />
    </Box>
  );
}

export default VoiceTestingInterface;